import { create } from 'zustand';

/**
 * A tiny global mirror of auto-save status, so chrome-level UI (e.g. the
 * AutoSaveBadge in the header) can render save state without re-running the
 * useAutoSave hook (which would open a duplicate socket session).
 *
 * The useAutoSave hook is responsible for pushing its internal saveStatuses
 * here whenever they change, and for installing/uninstalling a `retry`
 * callback that the badge can invoke when a save fails.
 *
 * This is deliberately separate from ideStore.ts to keep this store small and
 * cheap to subscribe to; ideStore changes on every keystroke (file content
 * updates) and we don't want every header re-render to ride that.
 */

export interface SaveStatusEntry {
  path: string;
  status: 'saving' | 'saved' | 'error';
  lastSaved?: Date;
  error?: string;
}

export type RetryFn = (path: string) => void;

interface SaveStatusState {
  statuses: SaveStatusEntry[];
  /** True only when the IDE page has mounted useAutoSave; outside the IDE the
   *  badge should hide itself rather than show a stale "Up to date". */
  active: boolean;
  retry: RetryFn | null;

  setStatuses: (statuses: SaveStatusEntry[]) => void;
  setActive: (active: boolean) => void;
  setRetry: (retry: RetryFn | null) => void;
  reset: () => void;
}

export const useSaveStatusStore = create<SaveStatusState>((set) => ({
  statuses: [],
  active: false,
  retry: null,

  setStatuses: (statuses) => set({ statuses }),
  setActive: (active) => set({ active }),
  setRetry: (retry) => set({ retry }),
  reset: () => set({ statuses: [], active: false, retry: null }),
}));
